import { useState, type ReactNode } from 'react';
import { Icon } from '../Icon.js';

export interface DetailSectionProps {
  title: string;
  titleRight?: ReactNode;
  /** @deprecated V2: la sección ES una tarjeta enmarcada; ya no hay divisor. */
  divider?: boolean;
  /** 'none' para cuerpos que van al ras (listas, tablas, sub-filas). */
  padding?: 'default' | 'none';
  /** Cabecera con subrayado cian (ej. "Ángulo de propuesta"). */
  accent?: boolean;
  /**
   * Plegable: la cabecera abre y cierra el cuerpo. Para lo que es dato y no
   * tarea (el riel del paso 3): una tarjeta cerrada dice que existe sin
   * competir con lo que hay que hacer. `defaultOpen` fija cómo arranca.
   */
  collapsible?: boolean;
  defaultOpen?: boolean;
  /**
   * Con clave, el navegador recuerda si la dejaste abierta o cerrada
   * (localStorage, por pestaña y proyecto). Sin clave, arranca en
   * `defaultOpen` cada vez.
   */
  storageKey?: string;
  /**
   * Cuántos elementos tiene adentro, al lado del título. En una tarjeta
   * plegable es lo que hace que cerrada siga diciendo algo: "Correos de la
   * lista (240)" en vez de un título que hay que abrir para entender.
   */
  count?: number;
  children: ReactNode;
}

function leerAbierta(key: string | undefined, defaultOpen: boolean): boolean {
  if (!key) return defaultOpen;
  try {
    const v = localStorage.getItem(`evo-seccion:${key}`);
    return v === null ? defaultOpen : v === '1';
  } catch { return defaultOpen; }
}

function guardarAbierta(key: string | undefined, abierta: boolean): void {
  if (!key) return;
  try { localStorage.setItem(`evo-seccion:${key}`, abierta ? '1' : '0'); } catch { /* sin almacenamiento */ }
}

/* V2 (handoff): toda sección de detalle es una TARJETA — borde --evo-stroke,
 * radio --evo-r-md, cabecera --evo-card-head-pad sobre --evo-bg-2 con título
 * en MAYÚSCULAS caption y acciones a la derecha, cuerpo --evo-card-pad.
 * El patrón viejo (borderTop + label suelto) queda reemplazado acá adentro:
 * mismos callers, look nuevo. */
export function DetailSection({
  title,
  titleRight,
  padding = 'default',
  accent = false,
  collapsible = false,
  defaultOpen = true,
  storageKey,
  count,
  children,
}: DetailSectionProps) {
  const [abierta, setAbierta] = useState(() => leerAbierta(storageKey, defaultOpen));
  const visible = !collapsible || abierta;
  const alternar = (): void => { setAbierta(v => { guardarAbierta(storageKey, !v); return !v; }); };
  return (
    <div className="evo-detail-card" style={{
      border: '1px solid var(--evo-stroke)',
      borderRadius: 'var(--evo-r-md)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}>
      <div
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? abierta : undefined}
        onClick={collapsible ? alternar : undefined}
        onKeyDown={collapsible ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); alternar(); } } : undefined}
        style={{
          padding: 'var(--evo-card-head-pad)',
          background: 'var(--evo-bg-2)',
          borderBottom: !visible
            ? 'none'
            : accent
              ? 'var(--evo-bw-hair) solid var(--evo-cyan)'
              : '1px solid var(--evo-stroke)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--evo-card-head-gap)',
          flexShrink: 0,
          cursor: collapsible ? 'pointer' : undefined,
          userSelect: collapsible ? 'none' : undefined,
        }}
      >
        <span style={{
          flex: 1,
          minWidth: 0,
          fontSize: 'var(--evo-fs-caption)',
          fontWeight: 'var(--evo-fw-medium)',
          letterSpacing: 'var(--evo-ls-caps)',
          color: 'var(--evo-fg-3)',
          textTransform: 'uppercase',
          fontFamily: 'var(--evo-font)',
        }}>
          {title}
          {count !== undefined && (
            <span style={{ fontWeight: 'var(--evo-fw-regular)', textTransform: 'none', marginLeft: 'var(--evo-space-1)' }}>({count})</span>
          )}
        </span>
        {titleRight != null && (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexShrink: 0 }}
            // Las acciones de la cabecera no pliegan la tarjeta.
            onClick={collapsible ? e => { e.stopPropagation(); } : undefined}
          >
            {titleRight}
          </div>
        )}
        {/* La flecha es la única señal de que la tarjeta se pliega: con el
            ▾ de texto en --evo-fg-4 no se veía, y Eduardo pidió acordeones
            para tarjetas que ya lo eran (2026-10-29). */}
        {collapsible && (
          <span aria-hidden style={{
            display: 'inline-flex',
            color: 'var(--evo-fg-3)',
            flexShrink: 0,
            transform: abierta ? 'rotate(180deg)' : 'none',
            transition: 'transform var(--evo-motion-collapse)',
          }}>
            <Icon name="chevron-down" size={14} strokeWidth={2} />
          </span>
        )}
      </div>
      {visible && (
        <div style={{ padding: padding === 'none' ? 0 : 'var(--evo-card-pad)', flex: 1 }}>
          {children}
        </div>
      )}
    </div>
  );
}
